import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import Cart from './Cart';
import ProductGrid from './ProductGrid';
import PaymentModal from './PaymentModal';
import CustomerSelect from './CustomerSelect';
import HoldSaleModal from './HoldSaleModal';
import ResumeSaleModal from './ResumeSaleModal';
import RecentSalesModal from './RecentSalesModal';
import CashierDashboard from '../dashboard/CashierDashboard';
import Modal from '../common/Modal';
import api from '../../services/api';
import { printReceipt } from '../../utils/printReceipt';
import '../../styles/pos.css';

const POSInterface = () => {
  const { 
    items,
    customer,
    addToCart,
    setCustomer,
    clearCart,
    loadHeldSale 
  } = useCart();
  
  const { user } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showRecentSalesModal, setShowRecentSalesModal] = useState(false);
  const [showMySalesModal, setShowMySalesModal] = useState(false);
  const [isMobileCartVisible, setIsMobileCartVisible] = useState(false);
  const [heldSales, setHeldSales] = useState([]);
  const [settings, setSettings] = useState({});
  
  const barcodeInputRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadHeldSales();
    // Focus barcode input
    barcodeInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (user?.business_id) {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      const response = await api.get(`/settings.php/business/${user.business_id}`);
      if (response.data) {
        setSettings({ 
          ...(response.data.business || {}), 
          ...(response.data.settings || {}) 
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm) {
        searchProducts();
      } else {
        loadProducts();
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, category]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/products.php', {
        params: { category: category !== 'all' ? category : undefined }
      });
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories.php');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadHeldSales = async () => {
    try {
      const response = await api.get('/pos.php/holds');
      setHeldSales(response.data.held_sales);
    } catch (error) {
      console.error('Failed to load held sales:', error);
    }
  };

  const searchProducts = async () => {
    if (!searchTerm) return;
    
    setLoading(true);
    try {
      const response = await api.get('/products.php/search', {
        params: { q: searchTerm, category: category !== 'all' ? category : undefined }
      });
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to search products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeScan = async (e) => {
    if (e.key === 'Enter') {
      const barcode = e.target.value;
      if (barcode) {
        try {
          const response = await api.get(`/products.php/barcode/${barcode}`);
          if (response.data) {
            addToCart(response.data);
            e.target.value = '';
          }
        } catch (error) {
          if (error.response?.status === 404) {
            alert('Product not found');
          } else {
            console.error('Error scanning barcode:', error);
          }
        }
      }
    }
  };

  const handleCheckout = () => {
    if (items.length === 0) {
      alert('Cart is empty');
      return;
    }
    setShowPayment(true);
  };

  const handleHoldSale = async () => {
    if (items.length === 0) {
      alert('Cart is empty');
      return;
    }
    setShowHoldModal(true);
  };

  const handleResumeSale = (sale) => {
    loadHeldSale(sale);
    setShowResumeModal(false);
  };

  const handlePaymentComplete = (saleData) => {
    // Automatically print receipt on complete instead of showing a preview modal
    printReceipt(saleData, user, settings);
    
    // Hide mobile cart after payment success
    setIsMobileCartVisible(false);
  };

  const handleReprintReceipt = (sale) => {
    printReceipt(sale, user, settings);
    setShowRecentSalesModal(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  return (
    <div className="pos-container">
      <div className="pos-header">
        <div className="pos-toolbar">
          <div className="search-section">
            <input
              ref={barcodeInputRef}
              type="text"
              placeholder="Scan barcode..."
              onKeyDown={handleBarcodeScan}
              className="barcode-input"
              disabled={loading}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              disabled={loading}
            />
          </div>

          <div className="filter-section">
            <select 
              value={category} 
              onChange={(e) => setCategory(e.target.value)}
              className="category-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.category_name}</option>
              ))}
            </select>

            <button 
              className="recent-sales-btn"
              onClick={() => setShowRecentSalesModal(true)}
            >
              Recent Sales
            </button>

            <button 
              className="my-sales-btn"
              style={{ padding: '0.5rem 1rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
              onClick={() => setShowMySalesModal(true)}
            >
              My Sales
            </button>

            <button 
              className="hold-sale-btn"
              onClick={handleHoldSale}
              disabled={items.length === 0}
            >
              Hold Sale
            </button>

            {heldSales.length > 0 && (
              <button 
                className="resume-sale-btn"
                onClick={() => setShowResumeModal(true)}
              >
                Resume Sale ({heldSales.length})
              </button>
            )}
          </div>
        </div>

        <div className="customer-info-bar">
          {customer ? (
            <div className="selected-customer">
              <span className="customer-name">
                {customer.first_name} {customer.last_name}
              </span>
              {customer.current_balance > 0 && (
                <span className="customer-balance">
                  Balance: {formatCurrency(customer.current_balance)}
                </span>
              )}
              <button 
                className="change-customer-btn"
                onClick={() => setShowCustomerSelect(true)}
              >
                Change
              </button>
            </div>
          ) : (
            <button 
              className="select-customer-btn"
              onClick={() => setShowCustomerSelect(true)}
            >
              + Add Customer
            </button>
          )}
        </div>
      </div>

      <div className="pos-content">
        <div className="products-section">
          {loading ? (
            <div className="loading-spinner">Loading...</div>
          ) : (
            <ProductGrid 
              products={products}
              onProductSelect={addToCart}
            />
          )}
        </div>

        <div className={`cart-section ${isMobileCartVisible ? 'mobile-visible' : ''}`}>
          {isMobileCartVisible && (
            <button 
              className="close-mobile-cart"
              onClick={() => setIsMobileCartVisible(false)}
            >
              × Close Cart
            </button>
          )}
          <Cart onCheckout={handleCheckout} />
        </div>
        
        {/* Floating Cart Button for Mobile */}
        <button 
          className="floating-cart-btn"
          onClick={() => setIsMobileCartVisible(true)}
        >
          🛒 <span className="cart-badge">{items.reduce((acc, item) => acc + item.quantity, 0)}</span>
        </button>
      </div>

      {/* Modals */}
      <PaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onPaymentComplete={handlePaymentComplete}
      />

      <CustomerSelect
        isOpen={showCustomerSelect}
        onClose={() => setShowCustomerSelect(false)}
        onSelect={setCustomer}
      />

      <HoldSaleModal
        isOpen={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        items={items}
      />

      <ResumeSaleModal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        heldSales={heldSales}
        onResume={handleResumeSale}
        onRefresh={loadHeldSales}
      />

      <RecentSalesModal
        isOpen={showRecentSalesModal}
        onClose={() => setShowRecentSalesModal(false)}
        onReprintReceipt={handleReprintReceipt}
      />

      <Modal
        isOpen={showMySalesModal}
        onClose={() => setShowMySalesModal(false)}
        title="My Sales Today"
      >
        <div style={{ minWidth: '400px' }}>
          <CashierDashboard />
        </div>
      </Modal>

    </div>
  );
};

export default POSInterface;